/**
 * AutoPartSpecsTabs
 * Renders 2 tabs on Auto Parts listing detail pages:
 *   1. Overview          - brand, part number, condition, fitment, warranty, origin
 *   2. Fitting & Ownership - install cost, expected lifespan, replacement interval,
 *                            EU tyre labels (when present)
 *
 * Data lives on `listing.part_specs`. When a field is missing we hide that row so
 * the tab stays clean; when the whole spec object is missing the component renders
 * nothing (the detail page falls back to its normal layout).
 */
import React from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Card, CardContent } from './ui/card';
import { Wrench, ClipboardList } from 'lucide-react';

const OVERVIEW_FIELDS = [
  { key: 'brand', label: 'Brand' },
  { key: 'part_number', label: 'Part Number / OEM Ref' },
  { key: 'condition_detail', label: 'Condition' },
  { key: 'fitment', label: 'Fitment' },
  { key: 'warranty', label: 'Warranty' },
  { key: 'origin', label: 'Country of Origin' },
];

const OWNERSHIP_FIELDS = [
  { key: 'install_cost', label: 'Estimated Fitting Cost' },
  { key: 'expected_lifespan', label: 'Expected Lifespan' },
  { key: 'replacement_interval', label: 'Replacement Interval' },
  { key: 'eu_wet_grip', label: 'EU Wet Grip Rating' },
  { key: 'eu_fuel_efficiency', label: 'EU Fuel Efficiency Rating' },
  { key: 'eu_noise', label: 'EU Noise Level' },
  { key: 'notes', label: 'Notes' },
];

const SpecRow = ({ label, value, testId }) => (
  <div
    className="flex justify-between items-start py-3 border-b border-gray-100 last:border-b-0"
    data-testid={testId}
  >
    <span className="text-sm font-medium text-gray-600 pr-4">{label}</span>
    <span className="text-sm text-gray-900 text-right font-semibold max-w-[60%]">
      {value}
    </span>
  </div>
);

const renderRows = (fields, specs, prefix) =>
  fields
    .filter((f) => specs[f.key] !== undefined && specs[f.key] !== null && specs[f.key] !== '')
    .map((f) => (
      <SpecRow
        key={f.key}
        label={f.label}
        value={specs[f.key]}
        testId={`${prefix}-${f.key}`}
      />
    ));

export const AutoPartSpecsTabs = ({ listing }) => {
  const specs = listing?.part_specs;
  if (!specs || typeof specs !== 'object' || Object.keys(specs).length === 0) {
    return null;
  }

  const overviewRows = renderRows(OVERVIEW_FIELDS, specs, 'auto-part-overview');
  const ownershipRows = renderRows(OWNERSHIP_FIELDS, specs, 'auto-part-ownership');

  if (overviewRows.length === 0 && ownershipRows.length === 0) return null;

  return (
    <Card className="mt-6" data-testid="auto-part-specs-card">
      <CardContent className="pt-6">
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid grid-cols-2 w-full mb-4" data-testid="auto-part-specs-tabs">
            <TabsTrigger value="overview" data-testid="tab-overview">
              <ClipboardList className="w-4 h-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="ownership" data-testid="tab-ownership">
              <Wrench className="w-4 h-4 mr-2" />
              Fitting &amp; Ownership
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" data-testid="tab-content-overview">
            {overviewRows.length > 0 ? (
              <div className="space-y-0">{overviewRows}</div>
            ) : (
              <p className="text-sm text-gray-500 py-6 text-center">
                No overview data provided for this listing yet.
              </p>
            )}
          </TabsContent>

          <TabsContent value="ownership" data-testid="tab-content-ownership">
            {ownershipRows.length > 0 ? (
              <div className="space-y-0">{ownershipRows}</div>
            ) : (
              <p className="text-sm text-gray-500 py-6 text-center">
                No fitting or ownership data provided for this listing yet.
              </p>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default AutoPartSpecsTabs;
